import {
  ElementCategoryID,
  type IElementCategory,
} from '../../../models/element-categories.model';
import { ElementKind, IElement } from '../../../models/project.model';
import {
  getElementPropertySelectionByValue,
  getValueFromConditionalNodes,
} from '../../../helpers/conditional-nodes.helpers';
import { moveLifecycleAResults } from '../../../helpers/conversion-factors.helpers';
import { Lifecycle } from '../../../models/lifecycles.model';
import {
  HeatingPropertyName,
  heatPumpDefaultCop,
  HEAT_PUMP_COP_DEFAULT_NAME,
  HeatingEnergyExpressions,
  HeatingProductsExpressions,
  HeaterProducts,
  DESIGN_OUTDOOR_TEMP_VARIABLE_NAME,
  HEAT_PUMP_ENERGY_VARIABLE_NAME,
  AUXILIARY_ENERGY_VARIABLE_NAME,
  HeatingType,
  AUX_HEATER_PRODUCT,
  AUX_ENERGY_PRODUCT,
  HeatTargetOptions,
  HEAT_DEFAULT_FALLBACK_COUNTS_NAME,
  heatDefaultFallbackCounts,
  HEAT_DEMAND_RECORD_NAME,
  ANNUAL_HEAT_DEMAND_VARIABLE_NAME,
} from './heating.model';
import { getDesignOutdoorTemp } from '../../../helpers/energy/city-climate.helpers';
import { getHeatPumpEnergyConsumption } from '../../../helpers/energy/heating-energy.helpers';
import { multiplyProperties, toNumber } from '../../../helpers/math.helpers';
import { conditionalHeaterProductId } from './conditions/heating-system-product-id';
import { conditionalEnergyProductId } from './conditions/energy-product-id';
import { heatingProperties } from './heating-properties';
import { isOneOf } from '../../../helpers/array.helpers';
import {
  getCount,
  getElementProperties,
  hasCount,
} from '../../../helpers/element-property.helpers';
import { getElementQuantityRecord } from '../../../helpers/element-quantity.helpers';
import { cacheFactory, required } from '../../../helpers/function.helpers';
import { ProductID } from '../../../models/product.model';
import { ENERGY_VARIABLE_NAME } from '@shared/helpers/energy/energy-expression-variables.models';
import { guards } from '@shared/helpers/typeguards.helpers';
import { ExpressionVariables } from '@shared/helpers/expression-variables.helpers';
import { EXPRESSION_VARIABLE_NAME } from '@shared/constants/expression-variables-names.constant';
import { getProjectGfa } from '@shared/helpers/project.helpers';
import { getElementPropertyResolvedCountByNameOrId } from '@shared/helpers/element-property.helpers';
import { isElementOfCategory } from '@shared/helpers/element-category.helpers';
import {
  type ElementPath,
  flattenElements,
  isBuildingVersionElement,
} from '@shared/helpers/recursive-element.helpers';
import { isDeactivated } from '@shared/helpers/element.helpers';
import { projectGuards } from '@shared/helpers/guards/project-guards.helpers';
import { IVL_B_PHASE_REFERENCE_PERIOD_YEARS } from '@shared/helpers/lifecycles.data';
import type { Baseline } from '@shared/models/project.model';

type HeatingDemandVariables = Record<HeatTargetOptions, number>;

const getEnergyVariables = (
  variables: ExpressionVariables,
): HeatingDemandVariables => {
  const space = variables[ENERGY_VARIABLE_NAME.HEATING_DEMAND_SPACE];
  const tapwater = variables[ENERGY_VARIABLE_NAME.HEATING_DEMAND_TAPWATER];
  if (guards.is.number(space) && guards.is.number(tapwater)) {
    return {
      [HeatTargetOptions.SpaceHeating]: space,
      [HeatTargetOptions.TapWater]: tapwater,
      [HeatTargetOptions.SpaceHeatingAndTapWater]: space + tapwater,
    };
  }
  return {
    [HeatTargetOptions.SpaceHeating]: 0,
    [HeatTargetOptions.TapWater]: 0,
    [HeatTargetOptions.SpaceHeatingAndTapWater]: 0,
  };
};

/**
 * Distribute total heating demand across the number of active heating instances
 */
const getHeatingVariables = (
  variables: ExpressionVariables,
): HeatingDemandVariables => {
  const totals = getEnergyVariables(variables);
  const heatingCount =
    variables[EXPRESSION_VARIABLE_NAME.ACTIVE_ELEMENTS_COUNT_BY_CATEGORY]?.[
      ElementCategoryID.Heating
    ] || 1;

  return multiplyProperties(totals, 1 / heatingCount);
};

/** Read heat target from element template defaults before properties are resolved into variables. */
const getHeatTargetFromElement = (element: IElement): HeatTargetOptions => {
  const property = getElementProperties(element).find(
    (p) => p.name === HeatingPropertyName.HeatTarget,
  );
  const count = getCount(property, false);
  if (isOneOf(Object.values(HeatTargetOptions), count)) {
    return count;
  }
  return HeatTargetOptions.SpaceHeatingAndTapWater;
};

/** Annual heat demand (kWh/yr) for the selected heat target — used before the energy quantity is resolved. */
const getAnnualHeatDemandForTarget = (
  variables: ExpressionVariables,
  heatTarget: HeatTargetOptions,
): number => getHeatingVariables(variables)[heatTarget];

/**
 * Add properties that are resolved before trying to resolve properties
 * @param variables
 * @returns
 */
const mapExpressionVariablesBefore: IElementCategory['mapExpressionVariablesBefore'] =
  (variables, element) => {
    const heatDemand = getHeatingVariables(variables);
    const heatTarget = getHeatTargetFromElement(element);
    const annualHeatDemand = getAnnualHeatDemandForTarget(
      variables,
      heatTarget,
    );
    const energyQuantity = getElementQuantityRecord(element)?.energy;
    const hasExplicitEnergyCount =
      energyQuantity !== undefined && hasCount(energyQuantity);

    return {
      ...variables,
      [HeatingPropertyName.Efficiency]: 1, // Make sure we have a default value
      [HEAT_PUMP_COP_DEFAULT_NAME]: heatPumpDefaultCop,
      [HEAT_DEFAULT_FALLBACK_COUNTS_NAME]: heatDefaultFallbackCounts,
      [HEAT_DEMAND_RECORD_NAME]: heatDemand,
      [ANNUAL_HEAT_DEMAND_VARIABLE_NAME]: annualHeatDemand,
      // Legacy max_capacity expressions use `energy`; only seed when quantity has no explicit count
      ...(hasExplicitEnergyCount ? {} : { energy: annualHeatDemand }),
      [DESIGN_OUTDOOR_TEMP_VARIABLE_NAME]: getDesignOutdoorTemp(
        variables.building_position,
      ),
    };
  };

/**
 * Add properties that are resolved after trying to resolve properties
 * @param variables
 * @returns
 */
const mapExpressionVariablesAfter: IElementCategory['mapExpressionVariablesAfter'] =
  (variables, element) => {
    const heatTarget =
      (variables[HeatingPropertyName.HeatTarget] as
        HeatTargetOptions | undefined) ?? getHeatTargetFromElement(element);

    const withSyncedAnnualHeatDemand = (
      vars: ExpressionVariables,
    ): ExpressionVariables => {
      const annualHeatDemand = getAnnualHeatDemandForTarget(vars, heatTarget);
      const energyQuantity = getElementQuantityRecord(element)?.energy;
      const hasExplicitEnergyCount =
        energyQuantity !== undefined && hasCount(energyQuantity);

      return {
        ...vars,
        [ANNUAL_HEAT_DEMAND_VARIABLE_NAME]: annualHeatDemand,
        ...(hasExplicitEnergyCount ? {} : { energy: annualHeatDemand }),
      };
    };

    // Only for heat pumps
    if (variables[HeatingPropertyName.Type] === HeatingType.HeatPump) {
      const energy = toNumber(variables['energy']);
      const heatPumpCapacity = toNumber(
        variables[HeatingPropertyName.HeatPumpCapacityPercent],
      );
      const cop = toNumber(variables[HeatingPropertyName.COP]);

      const { heatPumpEnergy, auxiliaryEnergy } = getHeatPumpEnergyConsumption(
        energy,
        heatPumpCapacity,
        cop,
      );
      return withSyncedAnnualHeatDemand({
        ...variables,
        [HEAT_PUMP_ENERGY_VARIABLE_NAME]: heatPumpEnergy,
        [AUXILIARY_ENERGY_VARIABLE_NAME]: auxiliaryEnergy,
        [HEAT_DEMAND_RECORD_NAME]: getHeatingVariables({
          ...variables,
          [HEAT_PUMP_ENERGY_VARIABLE_NAME]: heatPumpEnergy,
          [AUXILIARY_ENERGY_VARIABLE_NAME]: auxiliaryEnergy,
        }),
      });
    }

    // If not heat pump, set heat pump energy and auxiliary energy to 0 to make the variables still available for calculations
    return withSyncedAnnualHeatDemand({
      ...variables,
      [HEAT_PUMP_ENERGY_VARIABLE_NAME]: 0,
      [AUXILIARY_ENERGY_VARIABLE_NAME]: 0,
      [HEAT_DEMAND_RECORD_NAME]: getHeatingVariables({
        ...variables,
        [HEAT_PUMP_ENERGY_VARIABLE_NAME]: 0,
        [AUXILIARY_ENERGY_VARIABLE_NAME]: 0,
      }),
    });
  };

/**
 * B1.2 refrigerant leakage from heat pumps (kg CO2e per m² GFA and year).
 * Source: IVL, see DEV-3320.
 */
export const REFRIGERANT_CO2E_PER_M2_GFA_YEAR = 0.2;

const heatPumpProducts = [
  HeaterProducts.HeatPumpAirToAir,
  HeaterProducts.HeatPumpAirToWater,
  HeaterProducts.HeatPumpGeothermal,
  HeaterProducts.HeatPumpWater,
] as const;

/**
 * Check if an element is a heating element with a heat pump (köldmedieanläggning).
 */
const isHeatPumpElement = (element: IElement): boolean =>
  isElementOfCategory(element, ElementCategoryID.Heating) &&
  getElementPropertyResolvedCountByNameOrId(
    element,
    HeatingPropertyName.Type,
  ) === HeatingType.HeatPump;

/**
 * Count active heat pump heating elements in the version so the building-level
 * B1.2 refrigerant impact can be split between them (avoids double counting).
 */
const countActiveHeatPumpElements = (version: Baseline): number =>
  flattenElements(version)
    .filter(projectGuards.is.element)
    .filter((element) => !isDeactivated(element, false))
    .filter(isHeatPumpElement).length;

/**
 * Cached per version — the count is identical for every product row in the same pipeline pass.
 * Invalidates when the version reference changes (immutable project updates).
 */
const getActiveHeatPumpCountForVersion = (version: Baseline): number =>
  cacheFactory(
    () => Math.max(countActiveHeatPumpElements(version), 1),
    `activeHeatPumpCount[${version.id}]`,
    [version],
  );

const getActiveHeatPumpElementCount = (path: ElementPath): number => {
  const [root] = path;
  if (!root || !isBuildingVersionElement(root)) return 1;
  return getActiveHeatPumpCountForVersion(root);
};

const mapResults: IElementCategory['mapResults'] = (results, context) => {
  // We have two products so only move the energy results
  if (context.productElement.unit === 'kWh') {
    return moveLifecycleAResults(results, Lifecycle.B6);
  }

  // B1.2 refrigerant: only once per heat pump element, on the heat producer row.
  // Raw read instead of getGenericProductId, which throws for custom products.
  const isHeatPumpProducer = isOneOf(
    heatPumpProducts,
    context.productElement.generic_product_id ??
      context.productElement.product_id,
  );
  if (isHeatPumpProducer && isHeatPumpElement(context.categoryElement)) {
    const gfa = getProjectGfa(context.project);
    const refrigerantCO2e =
      (gfa *
        REFRIGERANT_CO2E_PER_M2_GFA_YEAR *
        IVL_B_PHASE_REFERENCE_PERIOD_YEARS) /
      getActiveHeatPumpElementCount(context.path);
    if (refrigerantCO2e) {
      return {
        ...results,
        'co2e_B1.2': refrigerantCO2e,
      };
    }
  }
  return results;
};

export const heating: IElementCategory = {
  id: ElementCategoryID.Heating,
  name: 'Heating',
  properties: heatingProperties,
  disableQuantitySelection: true,
  mapResults,
  mapExpressionVariablesBefore,
  mapExpressionVariablesAfter,

  getQuantityProperties: () => ({
    energy: {
      displayUnit: 'kWh/yr',
      displayName: 'Annual heat demand',
      fallbackCount: `${HEAT_DEMAND_RECORD_NAME}[${HeatingPropertyName.HeatTarget}]`, // max(1, variables[${EXPRESSION_VARIABLE_NAME.ACTIVE_ELEMENTS_COUNT_BY_CATEGORY}]['${ElementCategoryID.Heating}'])
    },
  }),

  getChildElements: (element) => {
    const heaterProductId = getValueFromConditionalNodes(element, [
      conditionalHeaterProductId,
    ]);
    const energyProductId = getValueFromConditionalNodes(element, [
      conditionalEnergyProductId,
    ]);

    return [
      // Heat producer
      {
        kind: ElementKind.Product,
        product_id: heaterProductId,
        count: HeatingProductsExpressions[heaterProductId],
        unit: 'kg',
      },
      // Energy consumption
      {
        kind: ElementKind.Product,
        product_id: energyProductId,
        count: required(
          HeatingEnergyExpressions[validateHeaterProductId(heaterProductId)],
        ),
        unit: 'kWh',
      },

      // (optional) Auxiliary heating
      {
        conditions: {
          [HeatingPropertyName.Type]: [HeatingType.HeatPump],
        },
        nodes: [AUX_HEATER_PRODUCT, AUX_ENERGY_PRODUCT],
      },
    ];
  },
  getElementPropertySelectionByProductId: (productId: ProductID) =>
    getElementPropertySelectionByValue(productId, conditionalHeaterProductId),
};

const validateHeaterProductId = (id?: string): HeaterProducts => {
  if (!isOneOf(Object.values(HeaterProducts), id)) {
    throw new Error(`Invalid heater product id: ${id}`);
  }
  return id as HeaterProducts;
};
